'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import { PulseButton } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import {
  useGetEngageStrategyListMembersPaginatedInfiniteQuery,
  useGetStrategyMetricsQuery,
} from 'src/api/redux/strategy';
import { useEngageStrategyConnector } from '../hooks/useEngageStrategyConnector';
import { getStrategyMetricDisplay } from 'src/components/Strategy/Profiles/utils/getStrategyMetricDisplay';

const PREVIEW_MAX_ITEMS = 2;
const PAGE_SIZE = 25;

const CompactEngageStrategyPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const engageStrategy = widgetConfig?.widget?.engage_strategy;
  const strategyId = engageStrategy?.id?.toString() ?? '';
  const strategyType = engageStrategy?.type ?? '';
  const { providerId: rawProviderId } = getEntityIdentifiers(entityDetails);
  const providerId = rawProviderId?.toString() || '';

  const {
    isConnectorLoading,
    isConnector404,
    isConnectorExecuting,
    isProcessing,
    isExecuting,
    connectorData,
    handleExecuteStrategy,
  } = useEngageStrategyConnector({ strategyId, providerId });

  const skipDataQueries = !strategyId || isConnector404 || isConnectorExecuting;

  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    { strategyType: 'engage', strategyId },
    { skip: skipDataQueries }
  );

  const sortMetric = useMemo(() => {
    if (!strategyMetrics?.results?.length) return null;
    return strategyMetrics.results[0]?.display_options?.[0]?.code || null;
  }, [strategyMetrics?.results]);

  const {
    data: paginatedData,
    isLoading,
    isError,
  } = useGetEngageStrategyListMembersPaginatedInfiniteQuery(
    {
      strategyId,
      provider_id: providerId,
      page_size: PAGE_SIZE,
      sort: sortMetric ? 'metric' : 'date_added',
      order: 'desc',
      ...(sortMetric && { sort_metric: sortMetric }),
    },
    { skip: !strategyId || !providerId || skipDataQueries }
  );

  const members = useMemo(
    () =>
      (paginatedData?.pages?.flatMap((page: any) => page.results) ?? []).slice(
        0,
        PREVIEW_MAX_ITEMS
      ),
    [paginatedData]
  );

  if (!engageStrategy?.id) return null;

  if (isProcessing) {
    const processingMessage =
      connectorData?.status === 'CREATED'
        ? 'Strategy queued...'
        : 'Running strategy...';

    return (
      <div
        className="flex flex-col items-center justify-center py-4 gap-2"
        role="status"
        aria-label="Strategy is processing"
        data-testid="compact-engage-strategy-preview-executing"
      >
        <div className="flex gap-1.5">
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:0ms]" />
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:150ms]" />
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:300ms]" />
        </div>
        <p className="text-sm font-medium text-neutral-500">
          {processingMessage}
        </p>
      </div>
    );
  }

  if (isConnectorLoading || isLoading) {
    return <WidgetLoading data-testid="compact-engage-strategy-preview-loading" aria-label="Loading strategy members" />;
  }

  if (isConnector404) {
    return (
      <div
        className="flex flex-col items-center justify-center py-3 gap-2.5"
        role="status"
        data-testid="compact-engage-strategy-preview-not-run"
      >
        <Icon
          icon={faClipboardList}
          className="text-neutral-300 h-4 w-4"
          aria-hidden="true"
        />
        <p className="text-xs text-neutral-400 text-center">
          Not yet run for this provider
        </p>
        <PulseButton
          variant="solid"
          color="primary"
          size="sm"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleExecuteStrategy();
          }}
          loading={isExecuting}
          aria-label="Execute engage strategy"
          className="whitespace-nowrap"
          data-testid="compact-engage-strategy-preview-execute-button"
        >
          Run Strategy
        </PulseButton>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex items-center justify-center py-4"
        role="alert"
        data-testid="compact-engage-strategy-preview-error"
      >
        <p className="text-sm text-neutral-500">
          Unable to load strategy results. Please contact your Customer Success
          team member for assistance.
        </p>
      </div>
    );
  }

  if (members.length === 0) {
    return <WidgetEmpty data-testid="compact-engage-strategy-preview-empty" />;
  }

  const metricLabel = strategyMetrics?.results?.[0]?.label || 'Metric';

  return (
    <div
      className="flex flex-col"
      data-testid="compact-engage-strategy-preview"
      role="list"
      aria-label="Engage Strategy Members"
    >
      <div className="flex items-center justify-end">
        <span className="text-[10px] font-semibold text-tertiary text-end">
          {metricLabel}
        </span>
      </div>
      {members.map((member: any) => {
        const metric = getStrategyMetricDisplay({
          type: strategyType,
          metrics: member.metrics,
          metricsV2: member.metrics_v2,
          strategyMetricResults: strategyMetrics?.results,
        });

        return (
          <div
            key={member.id || member.provider_id}
            className="flex items-center justify-between py-1 border-b border-neutral-100 last:border-b-0"
            role="listitem"
          >
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-medium text-primary truncate">
                {member.name}
              </span>
              {member.specialty && (
                <span className="text-[10px] text-secondary truncate">
                  {member.specialty}
                </span>
              )}
            </div>
            {metric.value != null && (
              <div className="flex items-center justify-end min-w-12">
                <span className="text-xs font-semibold text-teal-900 tabular-nums">
                  {metric.value}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompactEngageStrategyPreview;
